<script setup>
import { computed, ref, watch } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import { gavetaAbertaAqui } from 'dashboard/composables/usePainelDoContato';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

// 🔴 GAVETA OU COLUNA — e o limite não é mais o do celular (16/09/2026).
//
// Até aqui a ficha virava coluna a partir de 768px. Entre 768 e 1280 isso
// espremia a conversa: medido em 820px, sobravam 108px para ela e a bolha
// ficava com 60px, quebrando o texto letra a letra. Agora a coluna só existe
// quando há largura para as três; abaixo disso a ficha é gaveta sobre a
// conversa — aberta pelo mesmo botão, fechando ao clicar fora.
const estaEmGaveta = computed(
  () => windowWidth.value < wootConstants.CONTACT_PANEL_BREAKPOINT
);

// Gaveta só quando aberta neste aparelho — ver `usePainelDoContato.js`.
const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  if (!isContactSidebarOpen) return null;
  if (estaEmGaveta.value && !gavetaAbertaAqui.value) return null;
  return 0;
});

// O conteúdo só monta na PRIMEIRA abertura (24/10/2026): o painel agora fica montado com a
// conversa (para poder sair animado), e montar a ficha fechada buscaria os dados do contato
// à toa a cada troca de conversa.
const jaAbriu = ref(activeTab.value === 0);
watch(activeTab, aba => {
  if (aba === 0) jaAbriu.value = true;
});

const closeContactPanel = () => {
  if (estaEmGaveta.value) gavetaAbertaAqui.value = false;
  if (estaEmGaveta.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <!-- Véu do modo gaveta: sem ele a ficha paira sobre a conversa sem dizer que
       é uma camada, e o clique fora não parece ter alvo. Só existe enquanto a
       ficha é gaveta; virando coluna, some. -->
  <Transition name="cs-veu">
    <div
      v-if="estaEmGaveta && activeTab === 0"
      class="cs-veu"
      aria-hidden="true"
      @click="closeContactPanel"
    />
  </Transition>
  <!-- ABRIR E FECHAR COM CAMINHO (24/10/2026). Era troca de classe (`xl:hidden`): a coluna
       aparecia e sumia seca e a conversa mudava de largura de uma vez. Agora `v-show` dentro
       de um <Transition>: no computador o painel entra pela direita e a conversa encolhe junto
       (a margem anima, não a largura — o conteúdo não se espreme quadro a quadro); na gaveta,
       desliza de lado. Sai pelo mesmo caminho. -->
  <Transition name="cs-painel" appear>
    <div
      v-show="activeTab === 0"
      v-on-click-outside="() => closeContactPanel()"
      class="cs-painel bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm ltr:right-0 rtl:left-0 xl:static xl:w-[320px] xl:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg xl:shadow-none"
    >
      <div class="flex flex-1 overflow-auto">
        <ContactPanel
          v-if="jaAbriu"
          v-show="activeTab === 0"
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ── Celular ─────────────────────────────────────────────────────────────────
   🔴 A FICHA DO CONTATO ABRIA CORTADA EM CIMA. Relatado pelo Victor em
   08/09/2026 e medido no mesmo dia: o painel é `fixed top-0` com z-index 40, e
   a barra escura do topo (`.advup-topbar-safe-area`, `min-h-20` mais a área
   segura do aparelho) tem z-index 50. A barra ficava POR CIMA e comia os
   primeiros 80px do painel — o avatar do contato aparecia fatiado ao meio.

   No desktop isso nunca apareceu porque lá o painel é uma COLUNA ao lado da
   conversa, não uma camada sobre a tela.

   A conta usa os mesmos números da barra (`--advup-altura-topo` + área segura), para os dois
   continuarem casados se a barra mudar de altura. Acima de 640px, nada muda. */
/* O véu escurece a conversa atrás da gaveta — a mesma leitura do véu do modal,
   um degrau mais leve, porque aqui o que está atrás continua sendo o assunto. */
.cs-veu {
  position: fixed;
  inset: calc(var(--advup-altura-topo) + env(safe-area-inset-top)) 0 0;
  z-index: 39;
  background: rgb(15 23 42 / 32%);
  animation: cs-veu-entra var(--mov-2) var(--mov-curva);
}

.dark .cs-veu {
  background: rgb(0 0 0 / 50%);
}

@keyframes cs-veu-entra {
  from {
    opacity: 0;
  }
}

.cs-veu-leave-active {
  transition: opacity var(--mov-2) var(--mov-curva-saida);
}

.cs-veu-leave-to {
  opacity: 0;
}

/* ── ABRIR E FECHAR (24/10/2026) ─────────────────────────────────────────────
   Coluna (computador): a margem negativa do tamanho do painel recolhe a coluna e a conversa
   ocupa o espaço — animar a margem faz a conversa encolher enquanto o painel entra pela
   direita, sem espremer o conteúdo dele. Gaveta: desliza de lado, por onde volta. */
.cs-painel {
  --cs-largura: 320px;
}

.cs-painel-enter-active,
.cs-painel-leave-active {
  transition:
    margin-inline-end var(--mov-3) var(--mov-curva),
    transform var(--mov-3) var(--mov-curva),
    opacity var(--mov-2) var(--mov-curva);
}

.cs-painel-enter-from,
.cs-painel-leave-to {
  opacity: 0;
}

@media (min-width: 1280px) {
  .cs-painel-enter-from,
  .cs-painel-leave-to {
    margin-inline-end: calc(var(--cs-largura) * -1);
  }
}

@media (min-width: 1536px) {
  .cs-painel {
    --cs-largura: 360px;
  }
}

/* ── Modo GAVETA (abaixo de 1280px) ──────────────────────────────────────────
   Vale para celular E para a faixa onde a ficha deixou de ser coluna: em
   qualquer largura a barra escura do topo tem 80px e z-index 50, então a
   gaveta precisa começar abaixo dela — senão a barra come os primeiros 80px
   do painel e o avatar do contato aparece fatiado ao meio (relatado pelo
   Victor em 08/09/2026, no celular). */
@media (max-width: 1279px) {
  .cs-painel {
    top: calc(var(--advup-altura-topo) + env(safe-area-inset-top));
    height: calc(100% - var(--advup-altura-topo) - env(safe-area-inset-top));
  }

  /* A gaveta entra e sai pelo lado em que mora (direita; esquerda em RTL). */
  .cs-painel-enter-from,
  .cs-painel-leave-to {
    transform: translateX(100%);
  }

  [dir='rtl'] .cs-painel-enter-from,
  [dir='rtl'] .cs-painel-leave-to {
    transform: translateX(-100%);
  }
}

@media (max-width: 639px) {
  .cs-painel {
    /* TELA INTEIRA, não gaveta. `max-w-sm` (384px) é o desenho de desktop:
       numa tela de 402px ele deixava 18px da conversa aparecendo à esquerda —
       a faixa clara do print — e o conteúdo interno, que se mede pela tela e
       não pelo painel, terminava em x=402 e estourava a borda, cortando o
       botão de fechar. Medido em 402x874 (iPhone 17 Pro) em 08/09/2026. */
    max-width: none;
    width: 100%;

    /* Sem `padding-top` aqui: ele empurrava o conteúdo para dentro e deixava
       a página aparecer no topo do painel, virando uma linha branca entre a
       barra e a ficha. O respiro do botão de fechar sai do próprio hero. */
  }

  .cs-painel :deep(.cp-hero) {
    padding-top: var(--esp-2);
  }

  /* Fechar e abrir-em-nova-aba têm 28px, abaixo dos 32 que o padrão pede para
     toque. A área cresce por ::after, sem inchar o botão.
     ⚠️ NÃO declarar `position: relative` aqui: estes botões já são
     `absolute` (`top-3 right-3`), e sobrescrever a posição jogou um deles
     para x=-31, fora da tela — medido em 08/09/2026, meia hora depois de eu
     mesmo introduzir o defeito. `absolute` já serve de âncora para o ::after. */
  .cs-painel :deep(.cp-hero__acao)::after {
    content: '';
    position: absolute;
    inset: -6px;
  }
}
</style>
